import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";

// ── Constants ──────────────────────────────────────────────
const PAGE_W = 215.9;
const PAGE_H = 279.4;
const MARGIN = 12;
const CONTENT_W = PAGE_W - MARGIN * 2;
const FONT_SM = 8;
const FONT_MD = 8;
const FONT_LG = 9;
const LINE_H = 3.8;
const ROW_PAD = 1.5;
const COL_MID = MARGIN + CONTENT_W * 0.55;
const RIGHT_EDGE = PAGE_W - MARGIN;

// ── Helpers ────────────────────────────────────────────────
function generateBarcodeDataURL(value) {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, String(value), {
    format: "CODE128",
    width: 1.5,
    height: 40,
    displayValue: false,
    margin: 0,
  });
  return canvas.toDataURL("image/png");
}

function line(doc, x1, y1, x2, y2) {
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.line(x1, y1, x2, y2);
}

function rect(doc, x, y, w, h) {
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.rect(x, y, w, h);
}

function fitText(doc, text, maxW) {
  if (!text) return "";
  if (doc.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 0 && doc.getTextWidth(t + "...") > maxW) {
    t = t.slice(0, -1);
  }
  return t + "...";
}

function resetText(doc, size) {
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(size || FONT_SM);
  doc.setFont("helvetica", "normal");
}

function bold(doc) {
  doc.setFont("helvetica", "bold");
}

function normal(doc) {
  doc.setFont("helvetica", "normal");
}

// ── Full header (page 1 only) ─────────────────────────────
function drawFullHeader(doc, req, logoBase64, barcodeURL) {
  let y = drawTopBanner(doc, req, logoBase64, barcodeURL);
  y = drawClientAndCollection(doc, req, y);
  y = drawPatientBox(doc, req, y);
  y = drawInsurance(doc, req, y);
  y = drawClinicalInfo(doc, req, y);
  y = drawDiagnoses(doc, req, y);
  return y;
}

// ── Short header (continuation pages — everything except diagnoses) ──
function drawShortHeader(doc, req, logoBase64, barcodeURL) {
  let y = drawTopBanner(doc, req, logoBase64, barcodeURL);
  y = drawClientAndCollection(doc, req, y);
  y = drawPatientBox(doc, req, y);
  y = drawInsurance(doc, req, y);
  y = drawClinicalInfo(doc, req, y);
  y += 2;
  return y;
}

// ── Top banner (logo, barcode, req#, patient#) ────────────
function drawTopBanner(doc, req, logoBase64, barcodeURL) {
  let y = MARGIN;

  if (logoBase64) {
    try { doc.addImage(logoBase64, "PNG", MARGIN, y, 35, 14); } catch (e) {}
  }

  // Lab name + address centered between logo and barcode
  const centerX = PAGE_W / 2;
  bold(doc);
  doc.setFontSize(FONT_MD);
  doc.setTextColor(0, 0, 0);
  doc.text("Lincoln Reference Laboratory", centerX, y + 4, { align: "center" });
  normal(doc);
  doc.setFontSize(FONT_SM);
  doc.text("1023 Commerce Ave", centerX, y + 8, { align: "center" });
  doc.text("Union, New Jersey 07083", centerX, y + 11.5, { align: "center" });

  if (barcodeURL) {
    try { doc.addImage(barcodeURL, "PNG", RIGHT_EDGE - 42, y, 42, 12); } catch (e) {}
  }

  doc.setFontSize(FONT_MD);
  bold(doc);
  doc.text("Requisition #:", RIGHT_EDGE - 50, y + 15);
  normal(doc);
  doc.text(`${req.requisitionID}`, RIGHT_EDGE, y + 15, { align: "right" });
  bold(doc);
  doc.text("Patient #:", RIGHT_EDGE - 50, y + 19);
  normal(doc);
  doc.text(`${req.chartNumber || ""}`, RIGHT_EDGE, y + 19, { align: "right" });

  y += 22;
  line(doc, MARGIN, y, RIGHT_EDGE, y);
  y += 4;
  return y;
}

// ── Client + Collection ───────────────────────────────────
function drawClientAndCollection(doc, req, y) {
  const client = req.client || {};
  const physician = req.physician || {};
  resetText(doc, FONT_SM);

  const startY = y;

  bold(doc);
  doc.text("Client:", MARGIN, y);
  normal(doc);
  doc.text(`${req.clientCode || ""} - ${client.name || ""}`, MARGIN + 18, y);
  y += LINE_H;
  doc.text(`${client.address || ""} ${client.address1 || ""}`.trim(), MARGIN + 18, y);
  y += LINE_H;
  doc.text(`${client.city || ""}, ${client.state || ""} ${client.zip || ""}`, MARGIN + 18, y);
  y += LINE_H;
  bold(doc);
  doc.text("Phone:", MARGIN, y);
  normal(doc);
  doc.text(`${client.phone || ""}`, MARGIN + 18, y);
  y += LINE_H;
  bold(doc);
  doc.text("Ref. Phys:", MARGIN, y);
  normal(doc);
  const physText = physician.lastName
    ? `${physician.fullName} (${physician.npi || ""})`
    : "";
  doc.text(physText, MARGIN + 18, y);

  // Right side: collection info
  let ry = startY;
  const cvX = COL_MID + 28;
  bold(doc); doc.text("Collection Date:", COL_MID, ry); normal(doc); doc.text(`${req.collectionDate || ""}`, cvX, ry); ry += LINE_H;
  bold(doc); doc.text("Collection Time:", COL_MID, ry); normal(doc); doc.text(`${req.collectionTime || ""}`, cvX, ry); ry += LINE_H;
  bold(doc); doc.text("Fasting:", COL_MID, ry); normal(doc); doc.text(req.isFasting ? "Yes" : "No", cvX, ry); ry += LINE_H;
  bold(doc); doc.text("Order Type:", COL_MID, ry); normal(doc);
  if (req.orderType === "Stat") {
    doc.setFontSize(12); bold(doc); doc.text("STAT", cvX, ry); resetText(doc, FONT_SM);
  } else {
    doc.text(`${req.orderType || ""}`, cvX, ry);
  }

  y += LINE_H;
  return y;
}

// ── Patient Box ───────────────────────────────────────────
function drawPatientBox(doc, req, y) {
  resetText(doc, FONT_SM);
  const top = y;
  const pad = 1.5;

  bold(doc); doc.text("Patient Name:", MARGIN + pad, y + LINE_H); normal(doc);
  doc.text(`${req.lastName || ""}, ${req.firstName || ""}`, MARGIN + 22, y + LINE_H);
  doc.text(`${req.homeAddress || ""}`, MARGIN + 22, y + LINE_H * 2);
  doc.text(`${req.homeCity || ""} ${req.homeState || ""}, ${req.homeZip || ""}`, MARGIN + 22, y + LINE_H * 3);

  const bdY = y + LINE_H * 4.5;
  bold(doc); doc.text("Birth Date:", MARGIN + pad, bdY); normal(doc);
  doc.text(`${req.dob || ""}`, MARGIN + 22, bdY);
  bold(doc); doc.text("Sex:", MARGIN + 55, bdY); normal(doc);
  doc.text(`${req.sex || ""}`, MARGIN + 63, bdY);

  bold(doc); doc.text("Phone:", COL_MID + 2, y + LINE_H); normal(doc);
  doc.text(`${req.phone || ""}`, COL_MID + 15, y + LINE_H);
  bold(doc); doc.text("Email:", COL_MID + 2, y + LINE_H * 2); normal(doc);
  doc.text(`${req.email || ""}`, COL_MID + 15, y + LINE_H * 2);

  bold(doc); doc.text("Race:", COL_MID + 2, bdY); normal(doc);
  doc.text(`${req.race || "Unknown"}`, COL_MID + 13, bdY);
  bold(doc); doc.text("Ethnicity:", COL_MID + 38, bdY); normal(doc);
  doc.text(`${req.ethnicity || "Unknown"}`, COL_MID + 53, bdY);

  y += LINE_H * 5.5;
  rect(doc, MARGIN, top, CONTENT_W, y - top);
  line(doc, COL_MID, top, COL_MID, y);
  line(doc, MARGIN, bdY - LINE_H * 1.2, RIGHT_EDGE, bdY - LINE_H * 1.2);

  return y;
}

// ── Insurance ─────────────────────────────────────────────
function drawInsurance(doc, req, y) {
  resetText(doc, FONT_SM);
  const top = y;
  y += LINE_H;
  bold(doc); doc.text("Bill To:", MARGIN + 1.5, y); normal(doc);
  doc.text(`${req.billTo || ""}`, MARGIN + 18, y);

  if (req.billTo === "Insurance") {
    (req.insurances || []).forEach((ins) => {
      y += LINE_H;
      bold(doc); doc.text(ins.isPrimary ? "Primary:" : "Secondary:", MARGIN + 1.5, y); normal(doc);
      doc.text(`${ins.insCode || ""} - ${ins.insurance || ""}`, MARGIN + 18, y);
      bold(doc); doc.text("Rel. to ins:", MARGIN + 90, y); normal(doc);
      doc.text(`${ins.relationToInsured || ""}`, MARGIN + 106, y);
      bold(doc); doc.text("Policy:", COL_MID + 14, y); normal(doc);
      doc.text(`${ins.memberID || ""}`, COL_MID + 26, y);
      bold(doc); doc.text("Group:", COL_MID + 58, y); normal(doc);
      doc.text(`${ins.groupID || ""}`, COL_MID + 70, y);
    });
  }
  y += ROW_PAD;
  rect(doc, MARGIN, top, CONTENT_W, y - top);
  return y;
}

// ── Clinical Info ─────────────────────────────────────────
function drawClinicalInfo(doc, req, y) {
  resetText(doc, FONT_SM);
  const top = y;
  y += LINE_H;
  bold(doc); doc.text("Clinical Info:", MARGIN + 1.5, y); normal(doc);
  doc.text(`${req.clinicalInformation || ""}`, MARGIN + 22, y);
  y += LINE_H;
  bold(doc); doc.text("Comments:", MARGIN + 1.5, y); normal(doc);
  doc.text(`${req.comments || ""}`, MARGIN + 22, y);
  y += ROW_PAD;
  rect(doc, MARGIN, top, CONTENT_W, y - top);
  return y;
}

// ── Diagnoses (page 1 only) ──────────────────────────────
function drawDiagnoses(doc, req, y) {
  resetText(doc, FONT_LG);
  y += 3;
  bold(doc); doc.text("Diagnosis:", MARGIN, y); normal(doc);
  doc.setFontSize(FONT_SM);
  y += LINE_H;

  const diag = req.diagnoses || [];
  const half = Math.ceil(diag.length / 2);
  const left = diag.slice(0, half);
  const right = diag.slice(half);
  const rows = Math.max(left.length, right.length);

  const lCodeX = MARGIN + 2;
  const lDescX = MARGIN + 16;
  const lMaxW = COL_MID - lDescX - 4;
  const rCodeX = COL_MID + 2;
  const rDescX = COL_MID + 16;
  const rMaxW = RIGHT_EDGE - rDescX - 2;

  for (let i = 0; i < rows; i++) {
    if (left[i]) {
      doc.text(left[i].code || "", lCodeX, y);
      doc.text(fitText(doc, left[i].description || "", lMaxW), lDescX, y);
    }
    if (right[i]) {
      doc.text(right[i].code || "", rCodeX, y);
      doc.text(fitText(doc, right[i].description || "", rMaxW), rDescX, y);
    }
    y += LINE_H;
  }

  y += 2;
  return y;
}

// ── Test table headers ────────────────────────────────────
function drawTestHeaders(doc, y, colW, codeW, leftX, rightX) {
  const headerH = LINE_H + ROW_PAD * 2;

  // Draw both gray rects first
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.setFillColor(240, 240, 240);
  doc.rect(leftX, y, colW, headerH, "FD");
  doc.rect(rightX, y, colW, headerH, "FD");

  // Reset fill color immediately
  doc.setFillColor(255, 255, 255);

  // Now draw all text on top
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FONT_SM);

  // Left header text
  doc.text("Profile/Test", leftX + 2, y + LINE_H + 0.5);
  doc.text("Profile/Test Description", leftX + codeW + 2, y + LINE_H + 0.5);

  // Right header text
  doc.text("Profile/Test", rightX + 2, y + LINE_H + 0.5);
  doc.text("Profile/Test Description", rightX + codeW + 2, y + LINE_H + 0.5);

  // Divider lines inside headers
  line(doc, leftX + codeW, y, leftX + codeW, y + headerH);
  line(doc, rightX + codeW, y, rightX + codeW, y + headerH);

  doc.setFont("helvetica", "normal");

  return y + headerH;
}

// ── Test table with pagination ────────────────────────────
function drawTests(doc, req, logoBase64, barcodeURL, startY) {
  const tests = req.tests || [];
  if (tests.length === 0) return startY;

  const half = Math.ceil(tests.length / 2);
  const leftTests = tests.slice(0, half);
  const rightTests = tests.slice(half);
  const maxRows = Math.max(leftTests.length, rightTests.length);

  const gap = 4;
  const colW = (CONTENT_W - gap) / 2;
  const codeW = colW * 0.28;
  const descW = colW * 0.72;
  const rowH = LINE_H + ROW_PAD;
  const leftX = MARGIN;
  const rightX = MARGIN + colW + gap;

  let y = drawTestHeaders(doc, startY, colW, codeW, leftX, rightX);

  for (let i = 0; i < maxRows; i++) {
    // Page break
    if (y + rowH > PAGE_H - MARGIN - 15) {
      doc.addPage();
      // Short header on continuation pages (no diagnoses)
      y = drawShortHeader(doc, req, logoBase64, barcodeURL);
      y = drawTestHeaders(doc, y, colW, codeW, leftX, rightX);
    }

    const rowTop = y;
    resetText(doc, FONT_SM);

    if (leftTests[i]) {
      rect(doc, leftX, rowTop, colW, rowH);
      line(doc, leftX + codeW, rowTop, leftX + codeW, rowTop + rowH);
      doc.text(leftTests[i].code || "", leftX + 2, rowTop + LINE_H);
      doc.text(fitText(doc, leftTests[i].description || "", descW - 4), leftX + codeW + 2, rowTop + LINE_H);
    }

    if (rightTests[i]) {
      rect(doc, rightX, rowTop, colW, rowH);
      line(doc, rightX + codeW, rowTop, rightX + codeW, rowTop + rowH);
      doc.text(rightTests[i].code || "", rightX + 2, rowTop + LINE_H);
      doc.text(fitText(doc, rightTests[i].description || "", descW - 4), rightX + codeW + 2, rowTop + LINE_H);
    }

    y += rowH;
  }

  return y;
}

// ── Signature ─────────────────────────────────────────────
function drawSignature(doc, req, y) {
  const physician = req.physician || {};

  if (y + 30 > PAGE_H - MARGIN) {
    doc.addPage();
    y = MARGIN + 10;
  }

  y += 20;
  const sigX = PAGE_W - MARGIN - 85;
  resetText(doc, FONT_SM);

  doc.text(`Electronically signed by ${physician.fullName || ""}`, sigX, y);
  line(doc, sigX, y + 1, sigX + 55, y + 1);
  doc.text(new Date().toLocaleDateString("en-US"), sigX + 60, y);
  line(doc, sigX + 60, y + 1, sigX + 82, y + 1);

  y += LINE_H + 1;
  doc.text("Physician Signature", sigX, y);
  doc.text("Date", sigX + 60, y);
  return y;
}

// ── Page numbers ──────────────────────────────────────────
function addPageNumbers(doc) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(FONT_SM);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`page ${i} of ${total}`, PAGE_W / 2, PAGE_H - MARGIN + 3, { align: "center" });
  }
}

// ── Main export ───────────────────────────────────────────
export function generateRequisitionPDF(req, logoBase64) {
  const doc = new jsPDF({ unit: "mm", format: "letter", orientation: "portrait" });
  const barcodeURL = generateBarcodeDataURL(req.requisitionID);

  // Page 1: full header with diagnoses
  let y = drawFullHeader(doc, req, logoBase64, barcodeURL);

  // Tests (auto-paginates with short header on subsequent pages)
  y = drawTests(doc, req, logoBase64, barcodeURL, y);

  // Signature on last page
  drawSignature(doc, req, y);

  // Page numbers
  addPageNumbers(doc);

  return doc;
}